import type { LessonListing } from "@/features/workspaces/types/lessonListings";

import {
  addDays,
  endOfMonth,
  endOfWeek,
  isAfter,
  isBefore,
  isSameDay,
  startOfToday,
  startOfTomorrow,
  startOfWeek,
  startOfYesterday,
} from "date-fns";

/**
 * Group lessons by time periods with better past/future distinction
 */
export const groupLessonsByTimePeriod = (lessons: LessonListing[]) => {
  if (!lessons || lessons.length === 0) return {};

  const today = startOfToday();
  const yesterday = startOfYesterday();
  const tomorrow = startOfTomorrow();
  const endOfCurrentWeek = endOfWeek(today, { weekStartsOn: 1 });
  const startOfNextWeek = startOfWeek(addDays(today, 7), { weekStartsOn: 1 });
  const endOfNextWeek = endOfWeek(addDays(today, 7), { weekStartsOn: 1 });
  const endOfLastWeek = endOfWeek(addDays(today, -7), { weekStartsOn: 1 });
  const lastMonthEnd = endOfMonth(addDays(today, -30));

  const groups: Record<string, LessonListing[]> = {
    today: [],
    tomorrow: [],
    thisWeek: [],
    nextWeek: [],
    future: [],
    yesterday: [],
    lastWeek: [],
    lastMonth: [],
    older: [],
  };

  lessons.forEach((lesson) => {
    const lessonDate = new Date(lesson.start_time.replace(" ", "T"));

    // Set hours to 0 for date comparison
    const lessonDay = new Date(
      lessonDate.getFullYear(),
      lessonDate.getMonth(),
      lessonDate.getDate(),
    );

    // Future lessons
    if (isSameDay(lessonDay, today)) {
      groups.today.push(lesson);
    } else if (isSameDay(lessonDay, tomorrow)) {
      groups.tomorrow.push(lesson);
    } else if (isAfter(lessonDay, tomorrow) && isBefore(lessonDay, endOfCurrentWeek)) {
      groups.thisWeek.push(lesson);
    } else if (isAfter(lessonDay, startOfNextWeek) && isBefore(lessonDay, endOfNextWeek)) {
      groups.nextWeek.push(lesson);
    } else if (isAfter(lessonDay, endOfNextWeek)) {
      groups.future.push(lesson);
    }
    // Past lessons
    else if (isSameDay(lessonDay, yesterday)) {
      groups.yesterday.push(lesson);
    } else if (isBefore(lessonDay, yesterday) && isBefore(endOfLastWeek, lessonDay)) {
      groups.lastWeek.push(lesson);
    } else if (isBefore(lessonDay, endOfLastWeek) && isBefore(lastMonthEnd, lessonDay)) {
      groups.lastMonth.push(lesson);
    } else if (isBefore(lessonDay, lastMonthEnd)) {
      groups.older.push(lesson);
    }
  });

  // Remove empty groups
  Object.keys(groups).forEach((key) => {
    if (groups[key].length === 0) {
      delete groups[key];
    }
  });

  return groups;
};
